import { UserInfoFormData } from '@/types/user'

import { CpnPaymentReason } from './cpn'

export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  customValidator?: (value: string) => string | undefined
}

export interface CountryConfig {
  name: string
  code: string
  postalCodePattern: RegExp
  postalCodeFormat: string
  stateProvinceLabel: string
  stateProvinceRequired: boolean
  postalCodeLabel: string
}

// Country-specific configurations
export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  CA: {
    name: 'Canada',
    code: 'CA',
    postalCodePattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
    postalCodeFormat: 'A1A 1A1',
    stateProvinceLabel: 'Province',
    stateProvinceRequired: true,
    postalCodeLabel: 'Postal Code',
  },
  US: {
    name: 'United States',
    code: 'US',
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodeFormat: '12345 or 12345-6789',
    stateProvinceLabel: 'State',
    stateProvinceRequired: true,
    postalCodeLabel: 'ZIP Code',
  },
  GB: {
    name: 'United Kingdom',
    code: 'GB',
    postalCodePattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    postalCodeFormat: 'SW1A 1AA',
    stateProvinceLabel: 'County',
    stateProvinceRequired: false,
    postalCodeLabel: 'Postcode',
  },
  AU: {
    name: 'Australia',
    code: 'AU',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    stateProvinceLabel: 'State',
    stateProvinceRequired: true,
    postalCodeLabel: 'Postcode',
  },
  DE: {
    name: 'Germany',
    code: 'DE',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    stateProvinceLabel: 'State',
    stateProvinceRequired: false,
    postalCodeLabel: 'Postal Code',
  },
  FR: {
    name: 'France',
    code: 'FR',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    stateProvinceLabel: 'Region',
    stateProvinceRequired: false,
    postalCodeLabel: 'Postal Code',
  },
  JP: {
    name: 'Japan',
    code: 'JP',
    postalCodePattern: /^\d{3}-?\d{4}$/,
    postalCodeFormat: '123-4567',
    stateProvinceLabel: 'Prefecture',
    stateProvinceRequired: true,
    postalCodeLabel: 'Postal Code',
  },
  IN: {
    name: 'India',
    code: 'IN',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    stateProvinceLabel: 'State',
    stateProvinceRequired: true,
    postalCodeLabel: 'PIN Code',
  },
  MX: {
    name: 'Mexico',
    code: 'MX',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    stateProvinceLabel: 'State',
    stateProvinceRequired: true,
    postalCodeLabel: 'Postal Code',
  },
  BR: {
    name: 'Brazil',
    code: 'BR',
    postalCodePattern: /^\d{5}-?\d{3}$/,
    postalCodeFormat: '12345-678',
    stateProvinceLabel: 'State',
    stateProvinceRequired: true,
    postalCodeLabel: 'CEP',
  },
}

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Common validation patterns
export const VALIDATION_PATTERNS = {
  email: emailRegex,
  phone: /^[+]?[1-9][\d]{0,15}$/,
  alphaOnly: /^[\p{L}\s'-]+$/u,
  alphaNumeric: /^[\p{L}\p{N}\s'-]+$/u,
  noSpecialChars: /^[\p{L}\p{N}\s.,'/-\\]+$/u,
}

export interface BeneficiaryFormData {
  firstName: string
  lastName: string
  street: string
  city: string
  stateProvince: string
  postalCode: string
  country: string
  isBusiness: boolean
  isSelf: boolean
  paymentReason: CpnPaymentReason
}

export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

/**
 * Validates a single field based on rules and country context
 */
export const validateField = (
  fieldName: keyof BeneficiaryFormData,
  value: string | boolean,
  countryCode?: string
): string | undefined => {
  // Skip validation for boolean fields - they're always valid
  if (fieldName === 'isBusiness' || fieldName === 'isSelf') {
    return undefined
  }

  const stringValue = value as string
  const trimmedValue = stringValue.trim()

  switch (fieldName) {
    case 'firstName':
      if (!trimmedValue) return 'First name is required'
      if (trimmedValue.length < 2)
        return 'First name must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'First name must be less than 50 characters'
      if (!/^[\p{L}\s'-]+$/u.test(trimmedValue)) {
        return 'First name can only contain letters, spaces, hyphens, and apostrophes'
      }
      break

    case 'lastName':
      if (!trimmedValue) return 'Last name is required'
      if (trimmedValue.length < 2)
        return 'Last name must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'Last name must be less than 50 characters'
      if (!/^[\p{L}\s'-]+$/u.test(trimmedValue)) {
        return 'Last name can only contain letters, spaces, hyphens, and apostrophes'
      }
      break

    case 'street':
      if (!trimmedValue) return 'Street address is required'
      if (trimmedValue.length < 5)
        return 'Street address must be at least 5 characters'
      if (trimmedValue.length > 100)
        return 'Street address must be less than 100 characters'
      if (!VALIDATION_PATTERNS.noSpecialChars.test(trimmedValue)) {
        return 'Street address contains invalid characters'
      }
      break

    case 'city':
      if (!trimmedValue) return 'City is required'
      if (trimmedValue.length < 2) return 'City must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'City must be less than 50 characters'
      if (!/^[\p{L}\s'-]+$/u.test(trimmedValue)) {
        return 'City can only contain letters, spaces, hyphens, and apostrophes'
      }
      break

    case 'stateProvince': {
      const countryConfig = countryCode
        ? COUNTRY_CONFIGS[countryCode.toUpperCase()]
        : null
      const isRequired = countryConfig?.stateProvinceRequired ?? true

      if (isRequired && !trimmedValue) {
        const label = countryConfig?.stateProvinceLabel || 'State/Province'
        return `${label} is required`
      }
      if (trimmedValue && trimmedValue.length < 2) {
        return 'State/Province must be at least 2 characters'
      }
      if (trimmedValue && trimmedValue.length > 50) {
        return 'State/Province must be less than 50 characters'
      }
      if (trimmedValue && !/^[\p{L}\s'-]+$/u.test(trimmedValue)) {
        return 'State/Province can only contain letters, spaces, hyphens, and apostrophes'
      }
      break
    }

    case 'postalCode': {
      if (!trimmedValue) return 'Postal code is required'

      if (countryCode) {
        const config = COUNTRY_CONFIGS[countryCode.toUpperCase()]
        if (config) {
          if (!config.postalCodePattern.test(trimmedValue)) {
            return `Invalid ${config.postalCodeLabel.toLowerCase()} format. Expected format: ${config.postalCodeFormat}`
          }
        } else {
          // Generic postal code validation for unknown countries
          if (trimmedValue.length < 3 || trimmedValue.length > 10) {
            return 'Postal code must be between 3 and 10 characters'
          }
        }
      }
      break
    }

    case 'country':
      if (!trimmedValue) return 'Country code is required'
      if (trimmedValue.length !== 2)
        return 'Country must be a 2-letter ISO code (e.g., CA, US)'
      if (!/^[A-Z]{2}$/.test(trimmedValue))
        return 'Country code must be uppercase letters only'
      // You might want to validate against a list of valid country codes
      break

    case 'paymentReason':
      if (!trimmedValue) return 'Payment reason is required'
      break
  }

  return undefined
}

/**
 * Validates the entire form
 */
export const validateBeneficiaryForm = (
  formData: BeneficiaryFormData
): ValidationResult => {
  const errors: Record<string, string> = {}
  let isValid = true

  // Validate each field (skip boolean fields as they're always valid)
  ;(Object.keys(formData) as Array<keyof BeneficiaryFormData>).forEach(
    (fieldName) => {
      if (fieldName === 'isBusiness' || fieldName === 'isSelf') return

      const error = validateField(
        fieldName,
        formData[fieldName],
        formData.country
      )
      if (error) {
        errors[fieldName] = error
        isValid = false
      }
    }
  )

  return { isValid, errors }
}

/**
 * Gets country configuration for UI labels and hints
 */
export const getCountryConfig = (countryCode: string): CountryConfig | null => {
  return COUNTRY_CONFIGS[countryCode.toUpperCase()] || null
}

/**
 * Formats postal code according to country rules
 */
export const formatPostalCode = (
  value: string,
  countryCode: string
): string => {
  const config = getCountryConfig(countryCode)
  if (!config) return value

  const cleanValue = value.replace(/\s/g, '').toUpperCase()

  switch (countryCode.toUpperCase()) {
    case 'CA':
      // Format as A1A 1A1
      if (cleanValue.length >= 6) {
        return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3, 6)}`
      }
      return cleanValue

    case 'US':
      // Format as 12345-6789 if 9 digits
      if (cleanValue.length > 5) {
        return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5)}`
      }
      return cleanValue

    case 'GB':
      // Format UK postcodes with space
      if (cleanValue.length >= 5) {
        const lastThree = cleanValue.slice(-3)
        const firstPart = cleanValue.slice(0, -3)
        return `${firstPart} ${lastThree}`
      }
      return cleanValue

    default:
      return cleanValue
  }
}

/**
 * Sanitizes input by removing unwanted characters
 */
export const sanitizeInput = (
  value: string,
  type: 'name' | 'address' | 'alphaNumeric' = 'alphaNumeric'
): string => {
  switch (type) {
    case 'name':
      // Allow Unicode letters, spaces, apostrophes, hyphens, and accented characters
      return value.replace(/[^\p{L}\s'-]/gu, '')
    case 'address':
      // Allow Unicode letters, numbers, spaces, periods, commas, hyphens, apostrophes, and backslashes
      return value.replace(/[^\p{L}\p{N}\s.,'/-\\]/gu, '')
    default:
      return value.replace(/[^\p{L}\p{N}\s]/gu, '')
  }
}

// Metadata form data interface
export interface MetadataFormData {
  ACCOUNT_NUMBER: string
  ACCOUNT_TYPE: string
  BANK_NAME: string
  BANK_CODE: string
  BANK_COUNTRY: string
  CLABE: string
  RECIPIENT_ID_NUMBER: string
  RECIPIENT_PHONE_NUMBER: string
  SWIFT_CODE: string
  ORIGINATOR_NATIONALITY: string
  ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: string
  BENEFICIARY_DATE_OF_BIRTH: string
  BENEFICIARY_NATIONALITY: string
  BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER: string
  BENEFICIARY_PHONE_NUMBER: string
  BENEFICIARY_EMAIL: string
}

/**
 * Validates beneficiary metadata form fields
 */
export const validateMetadataField = (
  name: keyof MetadataFormData,
  value: string,
  isRequired: (fieldName: keyof MetadataFormData) => boolean
): string | undefined => {
  const trimmed = value.trim()
  if (!trimmed) {
    return isRequired(name) ? 'This field is required' : undefined
  }

  switch (name) {
    case 'BANK_COUNTRY':
      if (trimmed.length !== 2)
        return 'Country must be 2-letter code (e.g., HK, US)'
      if (!/^[A-Z]{2}$/.test(trimmed))
        return 'Country code must be uppercase letters only'
      break

    case 'SWIFT_CODE':
      if (trimmed.length < 8 || trimmed.length > 11)
        return 'SWIFT code must be 8-11 characters'
      if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(trimmed)) {
        return 'Invalid SWIFT format (e.g., HKBAHKHHXXX)'
      }
      break

    case 'CLABE':
      if (trimmed.length !== 18) return 'CLABE number must be exactly 18 digits'
      if (!/^\d{18}$/.test(trimmed))
        return 'CLABE number can only contain digits'
      break

    case 'ACCOUNT_NUMBER':
      if (trimmed.length < 3 || trimmed.length > 30)
        return 'Account number must be 3-30 characters'
      if (!/^[A-Z0-9\-./]+$/i.test(trimmed)) {
        return 'Account number can only contain letters, numbers, hyphens, dots, and slashes'
      }
      break

    case 'BANK_NAME':
      if (trimmed.length < 2 || trimmed.length > 100)
        return 'Bank name must be 2-100 characters'
      if (!/^[A-Za-z0-9\s\-.&,'()]+$/.test(trimmed)) {
        return 'Bank name contains invalid characters'
      }
      break

    case 'BANK_CODE':
      if (trimmed.length < 2 || trimmed.length > 15)
        return 'Bank code must be 2-15 characters'
      if (!/^[A-Z0-9]+$/i.test(trimmed)) {
        return 'Bank code can only contain letters and numbers'
      }
      break

    case 'RECIPIENT_ID_NUMBER':
    case 'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER':
    case 'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER':
      if (trimmed.length < 3 || trimmed.length > 30)
        return 'ID number must be 3-30 characters'
      if (!/^[A-Z0-9\-./\s]+$/i.test(trimmed)) {
        return 'ID number can only contain letters, numbers, hyphens, dots, slashes, and spaces'
      }
      break

    case 'ACCOUNT_TYPE':
      if (trimmed.length < 2 || trimmed.length > 50)
        return 'Account type must be 2-50 characters'
      if (!/^[A-Za-z\s-]+$/.test(trimmed)) {
        return 'Account type can only contain letters, spaces, and hyphens'
      }
      break

    case 'BENEFICIARY_EMAIL':
      if (!emailRegex.test(trimmed)) {
        return 'Please enter a valid email address'
      }
      break

    case 'BENEFICIARY_PHONE_NUMBER':
    case 'RECIPIENT_PHONE_NUMBER':
      if (!/^\+?[0-9\s\-()]{8,20}$/.test(trimmed)) {
        return 'Please enter a valid phone number (8-20 digits)'
      }
      break

    case 'BENEFICIARY_DATE_OF_BIRTH': {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return 'Date must be in YYYY-MM-DD format'
      }
      const date = new Date(trimmed)
      if (isNaN(date.getTime())) {
        return 'Please enter a valid date'
      }
      const eighteenYearsAgo = new Date()
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18)
      if (date > eighteenYearsAgo) {
        return 'Beneficiary must be at least 18 years old'
      }
      break
    }

    case 'BENEFICIARY_NATIONALITY':
    case 'ORIGINATOR_NATIONALITY':
      if (trimmed.length !== 2)
        return 'Nationality must be 2-letter country code (e.g., US, IN)'
      if (!/^[A-Z]{2}$/.test(trimmed))
        return 'Nationality code must be uppercase letters only'
      break
  }
  return undefined
}

// Field configuration for metadata fields
export const METADATA_FIELD_CONFIG: Record<
  string,
  {
    label: string
    placeholder: string
    maxLength?: number
    toUpperCase?: boolean
  }
> = {
  ACCOUNT_NUMBER: {
    label: 'Account Number',
    placeholder: 'e.g., 1234567890',
  },
  BANK_NAME: {
    label: 'Bank Name',
    placeholder: 'e.g., HSBC, Bank of America',
  },
  BANK_CODE: {
    label: 'Bank Code',
    placeholder: 'e.g., 004, 123',
  },
  BANK_COUNTRY: {
    label: 'Bank Country',
    placeholder: 'HK',
    maxLength: 2,
    toUpperCase: true,
  },
  CLABE: {
    label: 'CLABE Number',
    placeholder: 'e.g., 012345678901234567',
  },
  RECIPIENT_ID_NUMBER: {
    label: 'Recipient ID Number',
    placeholder: 'e.g., National ID, Passport number',
  },
  SWIFT_CODE: {
    label: 'SWIFT Code',
    placeholder: 'e.g., HKBAHKHHXXX',
    maxLength: 11,
    toUpperCase: true,
  },
}

// Validation rules
export const validateUserInfoField = (
  name: keyof UserInfoFormData,
  value: string
): string | undefined => {
  const trimmedValue = value.trim()

  switch (name) {
    case 'firstName':
      if (!trimmedValue) return 'First name is required'
      if (trimmedValue.length < 2)
        return 'First name must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'First name must be less than 50 characters'
      if (!/^[\p{L}\s'-]+$/u.test(trimmedValue))
        return 'First name can only contain letters, spaces, hyphens, and apostrophes'
      break
    case 'lastName':
      if (!trimmedValue) return 'Last name is required'
      if (trimmedValue.length < 2)
        return 'Last name must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'Last name must be less than 50 characters'
      if (!/^[\p{L}\s'-]+$/u.test(trimmedValue))
        return 'Last name can only contain letters, spaces, hyphens, and apostrophes'
      break
    case 'street':
      if (!trimmedValue) return 'Street address is required'
      if (trimmedValue.length < 5)
        return 'Street address must be at least 5 characters'
      if (trimmedValue.length > 100)
        return 'Street address must be less than 100 characters'
      break
    case 'city':
      if (!trimmedValue) return 'City is required'
      if (trimmedValue.length < 2) return 'City must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'City must be less than 50 characters'
      if (!/^[\p{L}\s'-]+$/u.test(trimmedValue))
        return 'City can only contain letters, spaces, hyphens, and apostrophes'
      break
    case 'stateProvince':
      if (!trimmedValue) return 'State/Province is required'
      if (trimmedValue.length < 2)
        return 'State/Province must be at least 2 characters'
      if (trimmedValue.length > 50)
        return 'State/Province must be less than 50 characters'
      break
    case 'postalCode':
      if (!trimmedValue) return 'Postal code is required'
      if (trimmedValue.length < 3)
        return 'Postal code must be at least 3 characters'
      if (trimmedValue.length > 10)
        return 'Postal code must be less than 10 characters'
      // Basic postal code format validation
      if (!/^[A-Za-z0-9\s-]+$/.test(trimmedValue))
        return 'Postal code can only contain letters, numbers, spaces, and hyphens'
      break
    case 'country':
      if (!trimmedValue) return 'Country is required'
      if (value.length !== 2)
        return 'Country must be 2-letter code (e.g., CA, US, HK)'
      if (!/^[A-Z]{2}$/.test(value))
        return 'Country code must be uppercase letters (e.g., CA, US, HK)'
      break
    case 'paymentReason':
      if (!trimmedValue) return 'Payment reason is required'
      break
  }
  return undefined
}

export const validateBenificiaryMetadataField = (
  name: keyof MetadataFormData,
  value: string
): string | undefined => {
  const trimmedValue = value.trim()
  if (!trimmedValue) return undefined // All metadata fields are optional

  switch (name) {
    case 'ACCOUNT_NUMBER':
      if (trimmedValue.length < 5)
        return 'Account number must be at least 5 characters'
      if (trimmedValue.length > 30)
        return 'Account number must be less than 30 characters'
      if (!/^[A-Za-z0-9-]+$/.test(trimmedValue))
        return 'Account number can only contain letters, numbers, and hyphens'
      break
    case 'BANK_NAME':
      if (trimmedValue.length < 2)
        return 'Bank name must be at least 2 characters'
      if (trimmedValue.length > 100)
        return 'Bank name must be less than 100 characters'
      if (!/^[a-zA-Z\s&.-]+$/.test(trimmedValue))
        return 'Bank name can only contain letters, spaces, and common punctuation (&, ., -)'
      if (/^\d+$/.test(trimmedValue)) return 'Bank name cannot be only numbers'
      break
    case 'BANK_CODE':
      if (trimmedValue.length < 3)
        return 'Bank code must be at least 3 characters'
      if (trimmedValue.length > 25)
        return 'Bank code must be less than 25 characters'
      if (!/^[A-Za-z0-9]+$/.test(trimmedValue))
        return 'Bank code can only contain letters and numbers'
      break
    case 'BANK_COUNTRY':
      if (value.length !== 2)
        return 'Bank country must be 2-letter code (e.g., HK, US)'
      if (!/^[A-Z]{2}$/.test(value))
        return 'Bank country code must be uppercase letters (e.g., HK, US)'
      break
    case 'CLABE':
      if (trimmedValue.length !== 18)
        return 'CLABE number must be exactly 18 digits'
      if (!/^\d{18}$/.test(trimmedValue))
        return 'CLABE number can only contain digits'
      break
    case 'RECIPIENT_ID_NUMBER':
      if (trimmedValue.length < 5)
        return 'Recipient ID must be at least 5 characters'
      if (trimmedValue.length > 50)
        return 'Recipient ID must be less than 50 characters'
      if (!/^[A-Za-z0-9-]+$/.test(trimmedValue))
        return 'Recipient ID can only contain letters, numbers, and hyphens'
      break
    case 'SWIFT_CODE':
      if (trimmedValue.length < 8 || trimmedValue.length > 11)
        return 'SWIFT code must be 8-11 characters'
      if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value))
        return 'Invalid SWIFT code format (e.g., HKBAHKHHXXX)'
      break
  }
  return undefined
}

export const validateEmail = (email: string) => {
  if (!email.trim()) {
    return { isValid: false, error: 'Email is required' }
  }

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' }
  }

  return { isValid: true, error: '' }
}
